import { Component, HostListener, OnDestroy, ViewChild, ElementRef, AfterViewInit, OnInit } from '@angular/core';
import { UserService } from '@app/services';
import { User } from '@app/model/user';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AnnouncementService } from '@app/services';
import { Announcement } from '@app/model/announcement';
import { TaskService } from '@app/services';
import { Task } from '@app/model/task';
import { LoadingComponent } from '@app/shared/loading/loading.component';
import { CategoryService } from '@app/services';
import { Category } from '@app/model/catetory';
import { SubCategoryService } from '@app/services';

@Component({
    selector: 'app-home-page',
    imports: [CommonModule, LoadingComponent],
    templateUrl: './home-page.component.html'
})

export class HomePageComponent implements OnInit, OnDestroy, AfterViewInit {
  user: User = new User('', '', '', '', '', '', '', 0, 0, 0, '', false, false, false, 0, '', '', '');
  announcements: Announcement[] = [];
  isLoading = false;
  isSmallScreen: boolean = false;
  tasks: Task[] = [];
  categories: Category[] = [];
  showSupply: boolean = false; // 改為 false 以顯示需求任務
  selectedCategoryId: string | null = null;
  categorySubCategoryMap: { [categoryId: string]: string[] } = {};
  
  // 滾動相關屬性
  @ViewChild('categoryScrollContainer') categoryScrollContainer!: ElementRef;
  canScrollLeft: boolean = false;
  canScrollRight: boolean = true;
  currentSlide = 0;
  autoSlideInterval: any;

  // 觸摸滑動相關屬性
  private touchStartX: number = 0;
  private touchEndX: number = 0;
  
  // 效能優化屬性
  private isPageVisible = true;
  private reducedMotion = false;
  private clickTimeout: any;
  private visibilityChangeListener: any;
  
  // 主視覺圖片
  heroBackgroundImage: string = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&h=800&q=80'; // 預設主視覺圖片

  @HostListener('window:resize', ['$event'])
  onResize = this.throttle((event: any) => {
    this.checkScreenSize(event.target.innerWidth);
  }, 100);

  constructor(
    private userService: UserService,
    private router: Router,
    private announcementService: AnnouncementService,
    private taskService: TaskService,
    private categoryService: CategoryService,
    private subCategoryService: SubCategoryService,
  ) { 
    // 檢測用戶是否偏好減少動畫
    if (typeof window !== 'undefined') {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  async ngOnInit() {
    this.isLoading = true;
    this.checkScreenSize(window.innerWidth);

    try {
      // 設置效能優化監聽器和觀察器
      this.setupVisibilityListener();

      // 並行獲取所有數據
      const [userResult, announcementsResult, tasksResult, categoriesResult] = await Promise.allSettled([
        this.userService.getUser(),
        this.announcementService.getAllAnnouncements(),
        this.taskService.getGroupedAcceptableTasks(),
        this.categoryService.getAvailableCategories()
      ]);

      if (userResult.status === 'fulfilled') {
        this.user = userResult.value?.data?.[0] || this.user;
      } else {
        console.error('取得使用者資料失敗：', userResult.reason);
      }

      if (announcementsResult.status === 'fulfilled') {
        this.announcements = this.sortAnnouncements(announcementsResult.value || []);
        this.currentSlide = 0;
      } else {
        console.error('取得公告資料失敗：', announcementsResult.reason);
      }

      if (tasksResult.status === 'fulfilled') {
        this.tasks = tasksResult.value?.data || [];
      } else {
        console.error('取得任務資料失敗：', tasksResult.reason);
      }

      if (categoriesResult.status === 'fulfilled') {
        this.categories = categoriesResult.value || [];
      } else {
        console.error('取得類別資料失敗：', categoriesResult.reason);
      }
      
      // 獲取分類與子分類的對應關係
      await this.loadCategorySubCategoryMap();
      
      // 只在用戶允許動畫且有多個公告時開始輪播
      if (!this.reducedMotion && this.announcements.length > 1) {
        this.startAutoSlide();
      }
    } catch (error) {
      console.error('獲取數據時發生錯誤：', error);
    } finally {
      this.isLoading = false;
    }
  }

  viewAnnouncementDetail(announcement: Announcement) {
    // 添加節流控制，防止快速連續點擊
    if (this.clickTimeout) {
      return;
    }
    
    this.clickTimeout = setTimeout(() => {
      this.clickTimeout = null;
    }, 300);
    
    this.router.navigate(['/announcement-detail', announcement.id]);
  }

  sortAnnouncements(announcements: Announcement[]) {
    return announcements.sort((a, b) => {
      if (a.is_pin && !b.is_pin) {
        return -1;
      } else if (!a.is_pin && b.is_pin) {
        return 1;
      } else {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });
  }

  checkScreenSize(width: number) {
    this.isSmallScreen = width < 768;
  }

  startAutoSlide() {
    // 清除現有的定時器
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
    
    // 在減少動畫模式下跳過自動輪播
    if (this.reducedMotion) {
      return;
    }
    
    // 只在頁面可見且有多個公告時啟動
    if (this.isPageVisible && this.announcements.length > 1) {
      this.autoSlideInterval = setInterval(() => {
        if (this.isPageVisible && document.visibilityState === 'visible') {
          this.nextSlide();
        }
      }, 6000); // 統一間隔時間
    }
  }

  stopAutoSlide() {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = null;
    }
  }

  nextSlide() {
    if (this.announcements.length > 0) {
      this.currentSlide = (this.currentSlide + 1) % this.announcements.length;
    }
  }

  prevSlide() {
    if (this.announcements.length > 0) {
      this.currentSlide = this.currentSlide === 0 ? this.announcements.length - 1 : this.currentSlide - 1;
    }
  }

  goToSlide(index: number) {
    this.currentSlide = index;
  }



  toggleTaskType(isSupply: boolean) {
    this.showSupply = isSupply;
  }

  selectCategory(categoryId: string | null) {
    this.selectedCategoryId = categoryId;
    // 滾動到選中的分類
    setTimeout(() => {
      this.scrollToSelectedCategory();
    }, 100);
  }

  getSelectedCategoryName(): string {
    if (!this.selectedCategoryId) return '全部分類';
    const category = this.categories.find(c => c.id === this.selectedCategoryId);
    return category ? category.title : '未知分類';
  }

  getSelectedCategoryColor(): string {
    if (!this.selectedCategoryId) return 'var(--app-color-surface-panel)';
    return this.categories.find(category => category.id === this.selectedCategoryId)?.color || 'var(--app-color-brand)';
  }

  getContrastingTextColor(backgroundColor: string): string {
    if (backgroundColor.startsWith('var(')) {
      return backgroundColor === 'var(--app-color-brand)'
        ? 'var(--app-color-surface)'
        : 'var(--app-color-ink)';
    }

    const hex = (backgroundColor || 'ffffff').replace('#', '');
    const normalizedHex = hex.length === 3
      ? hex.split('').map(character => character + character).join('')
      : hex;
    const r = parseInt(normalizedHex.substring(0, 2), 16);
    const g = parseInt(normalizedHex.substring(2, 4), 16);
    const b = parseInt(normalizedHex.substring(4, 6), 16);

    if ([r, g, b].some(value => Number.isNaN(value))) return 'var(--app-color-ink)';

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 150 ? 'var(--app-color-ink)' : 'var(--app-color-surface)';
  }

  getFilteredTasks(): Task[] {
    // 任務已經是分組和篩選後的，直接使用
    const availableTasks = this.tasks;
    
    // 根據供給/需求類型篩選
    let filteredTasks = availableTasks.filter(task => task.is_supply === this.showSupply);
    
    if (this.selectedCategoryId && this.categorySubCategoryMap[this.selectedCategoryId]) {
      // 使用已載入的子分類ID來篩选任務
      const subCategoryIds = this.categorySubCategoryMap[this.selectedCategoryId];
      filteredTasks = filteredTasks.filter(task => 
        subCategoryIds.includes(task.sub_category)
      );
    }
    
    // 使用記憶體優化的篩選
    return this.optimizedTaskFilter(filteredTasks)
      .sort((a, b) => new Date(b.createAt).getTime() - new Date(a.createAt).getTime());
  }

  createNewTask() {
    this.router.navigate(['/create-task-category-page']);
  }

  navigateTo(path: string) {
    this.router.navigate([path]);
  }

  viewTaskDetail(task: Task) {
    // 添加節流控制，防止快速連續點擊
    if (this.clickTimeout) {
      return;
    }
    
    this.clickTimeout = setTimeout(() => {
      this.clickTimeout = null;
    }, 300);
    
    this.router.navigate(['/task-detail', task.id]);
  }

  viewMoreTasks() {
    if (this.selectedCategoryId) {
      this.router.navigate(['/list-task', this.selectedCategoryId]);
    } else {
      this.router.navigate(['/list-task']);
    }
  }

  getCategoryIconPath(iconUrl: string): string {
    return iconUrl ? `/public/icons/${iconUrl}` : '/public/icons/其他.png';
  }

  async loadCategorySubCategoryMap() {
    try {
      // 一次性獲取所有子分類，避免 N+1 查詢問題
      const allSubCategoriesResponse = await this.subCategoryService.getAllSubCategory();
      const allSubCategories = allSubCategoriesResponse?.data || [];

      // 在記憶體中分組子分類
      for (const category of this.categories) {
        this.categorySubCategoryMap[category.id] = allSubCategories
          .filter(sub => sub.parent_category_id === category.id)
          .map(sub => sub.id);
      }

      // 分類載入完成後更新滾動指示器
      setTimeout(() => {
        this.updateScrollIndicators();
        this.updateScrollButtons();
      }, 100);
    } catch (error) {
      console.error('載入分類子分類對應關係時發生錯誤：', error);
    }
  }

  ngAfterViewInit() {
    // 初始化滾動指示器
    setTimeout(() => {
      this.updateScrollIndicators();
      this.updateScrollButtons();
    }, 100);
  }

  updateScrollIndicators() {
    // 不再需要指示器功能
  }

  updateScrollButtons() {
    if (this.categoryScrollContainer) {
      const container = this.categoryScrollContainer.nativeElement;
      this.canScrollLeft = container.scrollLeft > 0;
      this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth);
    }
  }

  scrollCategories(direction: 'left' | 'right') {
    if (this.categoryScrollContainer) {
      const container = this.categoryScrollContainer.nativeElement;
      const scrollAmount = 180;
      
      if (direction === 'left') {
        container.scrollLeft -= scrollAmount;
      } else {
        container.scrollLeft += scrollAmount;
      }
    }
  }

  onCategoryScroll = this.throttle(() => {
    this.updateScrollButtons();
  }, 50);

  onTouchStart(event: TouchEvent) {
    this.touchStartX = event.touches[0].clientX;
  }

  onTouchMove(event: TouchEvent) {
    this.touchEndX = event.touches[0].clientX;
  }

  onTouchEnd() {
    const touchDiff = this.touchStartX - this.touchEndX;
    const minSwipeDistance = 50;

    if (Math.abs(touchDiff) > minSwipeDistance) {
      if (touchDiff > 0) {
        // 向左滑動，滾動到右邊
        this.scrollCategories('right');
      } else {
        // 向右滑動，滾動到左邊
        this.scrollCategories('left');
      }
    }
  }

  // 自動滾動到選中的分類
  scrollToSelectedCategory() {
    if (this.categoryScrollContainer && this.selectedCategoryId) {
      const container = this.categoryScrollContainer.nativeElement;
      const categories = container.querySelectorAll('.category-item-enhanced');
      
      categories.forEach((element: Element) => {
        if (element.classList.contains('selected')) {
          const elementLeft = (element as HTMLElement).offsetLeft;
          const containerWidth = container.clientWidth;
          const elementWidth = (element as HTMLElement).offsetWidth;
          const scrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);
          
          container.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
        }
      });
    }
  }

  ngOnDestroy() {
    // 清理所有定時器
    this.stopAutoSlide();
    
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
    }
    
    // 移除頁面可見性監聽器
    if (this.visibilityChangeListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    }
  }
  
  // ===== 圖片錯誤處理方法 =====
  
  /**
   * 主視覺圖片載入錯誤處理
   */
  onHeroImageError(event: any): void {
    console.warn('主視覺圖片載入失敗，使用預設圖片');
    event.target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&h=800&q=80';
  }

  /**
   * 任務圖片載入錯誤處理
   */
  onTaskImageError(event: any): void {
    console.warn('任務圖片載入失敗，使用預設任務圖片');
    event.target.src = '/public/assets/defaults/default_task_image.png';
  }

  /**
   * 獲取任務顯示文本（包含庫存信息）
   * @param task 任務對象
   * @returns 顯示文本
   */
  getTaskDisplayText(task: Task): string {
    if (task.availableStock !== undefined && task.availableStock > 1) {
      return `${task.title} (庫存：${task.availableStock})`;
    }
    return task.title;
  }

  // ===== 效能優化相關方法 =====

  /**
   * 設置頁面可見性監聽器
   */
  private setupVisibilityListener() {
    if (typeof document === 'undefined') return;
    
    this.visibilityChangeListener = () => {
      this.isPageVisible = document.visibilityState === 'visible';
      
      if (this.isPageVisible) {
        // 頁面重新可見時恢復動畫
        if (!this.reducedMotion && this.announcements.length > 1) {
          this.startAutoSlide();
        }
      } else {
        // 頁面隱藏時停止動畫
        this.stopAutoSlide();
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeListener);
  }

  /**
   * 記憶體優化的任務篩選
   */
  private optimizedTaskFilter(tasks: Task[]): Task[] {
    const maxTasks = 12; // 顯示12個任務
    
    const filtered = tasks
      .filter(task => {
        // 基本篩選邏輯
        const statusCheck = task.status === '已發起' || task.status === '媒合進行中';
        const creatorCheck = task.creatorId !== this.user?.id;
        return statusCheck && creatorCheck;
      })
      .slice(0, maxTasks);
      
    return filtered;
  }

  /**
   * 節流函數 - 防止過度觸發
   */
  private throttle<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
    let timeoutId: NodeJS.Timeout | null = null;
    let lastExecTime = 0;
    
    return (...args: Parameters<T>) => {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }

  /**
   * TrackBy 函數用於優化 ngFor 效能
   */
  trackByCategory(index: number, category: Category): string {
    return category.id;
  }

  trackByTask(index: number, task: Task): string {
    return task.id;
  }

  trackByAnnouncement(index: number, announcement: Announcement): string {
    return announcement.id;
  }
}
